/**
 * Timeline conversion for telemetry marks.
 *
 * Marks are collected on `performance.now()` — under a blocked main thread the
 * wall clock and the monotonic clock drift apart, so a mix of the two produces
 * spans that no longer line up. Conversion happens once, at report time.
 *
 * The baseline comes from avatarkit's clock calibration when available, which
 * puts RTC spans on the same timeline as avatarkit's own and as the server's
 * records. avatarkit is a peer dependency and may be an older version without
 * that entry point, so the import is optional and resolved lazily; callers get
 * null and fall back to a local anchor.
 *
 * @packageDocumentation
 * @internal
 */

import { logger } from './logger';

type ResolveMono = (mono: number) => number;

let resolver: ResolveMono | null = null;
let resolved = false;

/**
 * Load avatarkit's calibrated clock once. Kept out of the module's import graph
 * so a host on an older avatarkit still loads this SDK.
 * @internal
 */
async function loadResolver(): Promise<void> {
  if (resolved) return;
  resolved = true;
  try {
    // Held in a variable so the bundler cannot statically resolve it: the
    // entry only exists on avatarkit >= 1.3.1, and a build-time resolution
    // failure would break the whole SDK rather than degrading this one path.
    const entry = '@spatius/avatarkit/internal-telemetry';
    const mod = (await import(/* @vite-ignore */ entry)) as {
      resolveMonoTimestamp?: ResolveMono;
    };
    if (typeof mod.resolveMonoTimestamp === 'function') {
      resolver = mod.resolveMonoTimestamp;
      return;
    }
    logger.info(
      'Clock',
      'avatarkit exposes no calibrated clock; telemetry falls back to the local one',
    );
  } catch {
    logger.info(
      'Clock',
      'avatarkit calibrated clock unavailable; telemetry falls back to the local one',
    );
  }
}

/** Begin loading the calibrated clock. Safe to call more than once. @internal */
export function initializeClock(): void {
  void loadResolver();
}

/**
 * Convert a `performance.now()` reading into a timeline timestamp (epoch ms).
 * Returns null until avatarkit's calibrated clock is available, leaving the
 * caller to apply its own anchor.
 * @internal
 */
export function resolveMonoTimestamp(mono: number): number | null {
  if (!resolver) return null;
  try {
    return resolver(mono);
  } catch {
    return null;
  }
}
